export type SubscriptionAction = 'subscribe' | 'unsubscribe';

export interface VideoSubscriptionManagerOptions {
    /** Called when a member should start receiving video (a new WebRTC peer). */
    onSubscribe: (memberName: string) => void;
    /** Called when a member's video should be torn down. */
    onUnsubscribe: (memberName: string) => void;
    /**
     * Continuous time a newly-visible member must stay visible before we
     * subscribe (ms). A short gate: appearing tiles should light up quickly, but
     * a tile that flickers in and out during a layout reshuffle shouldn't cause a
     * subscribe it immediately undoes.
     */
    subscribeDelayMs: number;
    /**
     * Continuous time a member must stay hidden before we unsubscribe (ms).
     * Deliberately longer than the subscribe gate: keep a peer alive briefly after
     * its tile leaves the visible set so a speaker bouncing in and out (or a
     * transient overflow) doesn't thrash the connection.
     */
    unsubscribeDelayMs: number;
    /**
     * Minimum time between two actions on the *same* member (ms). A hard floor on
     * how often a single member's peer can be rebuilt/torn down, independent of
     * the debounce gates — the last line of defence against sub/unsub spam when
     * the visible set oscillates around a member.
     */
    minResubscribeMs: number;
    /**
     * How long a held subscription's peer must stay reported dead (via
     * {@link VideoSubscriptionManager.reportPeerLiveness}) before the manager
     * treats the subscription as externally torn down and re-converges (ms).
     * Must comfortably exceed a legitimate peer rebuild's close→create window
     * so a restart never reads as a loss; a real loss (reconnect) stays dead
     * forever, so a generous grace only delays recovery, never misses it.
     */
    peerLostGraceMs: number;
    /**
     * Optional sink for human-readable flow logs — the actual subscribe/
     * unsubscribe decisions and the debounce cancellations that prove a flicker
     * did *not* cause churn. Left undefined by tests (pure, side-effect-free);
     * wired to the meeting log in production. Never logs on a bare tick that does
     * nothing, so it stays quiet unless the subscription state actually moves.
     */
    log?: (message: string) => void;
}

export interface MemberSubscriptionState {
    /** Whether we currently hold a subscription (issued subscribe, not yet undone). */
    subscribed: boolean;
    /**
     * When the current pending run began — the moment the desired state started
     * disagreeing with `subscribed` — or null when the member is settled. Used to
     * measure how long the disagreement has held for the debounce gate.
     */
    pendingSince: number | null;
    /** The last action we actually emitted for this member, or null if none yet. */
    lastAction: SubscriptionAction | null;
    /** When {@link lastAction} was emitted (ms, same clock as passed to methods). */
    lastActionAt: number | null;
    /**
     * Whether a live peer has been observed (reportPeerLiveness) since our last
     * emitted action. Loss-detection only arms once this is true, so a
     * subscription whose peer setup is still in flight is never mistaken for a
     * torn-down one.
     */
    peerSeenLive: boolean;
    /**
     * When the peer of a held subscription was first reported dead after having
     * been seen live, or null while it is live (or was never seen live). Once
     * the report has stayed dead for peerLostGraceMs, the subscription is
     * demoted — see {@link VideoSubscriptionManager.reportPeerLiveness}.
     */
    peerLostSince: number | null;
}

export interface VideoSubscriptionManager {
    /**
     * Declare the set of members that should currently be subscribed (the visible
     * tiles). Order is irrelevant — only membership matters. Starts or cancels
     * per-member debounce runs and then flushes any actions that are now due.
     * `nowMs` must be a monotonic clock reading (e.g. performance.now()).
     */
    setVisibleMembers(memberNames: string[], nowMs: number): void;
    /**
     * Advance time without changing the visible set: emits any (un)subscribe whose
     * debounce (or rate-limit) window has since elapsed. Call this on a timer so a
     * pending action still fires when no visibility change arrives to drive it.
     */
    tick(nowMs: number): void;
    /**
     * Report whether a subscribed member's receiver peer is actually alive in
     * the outside world. The manager holds no reference to the store, so
     * without this input it would keep believing `subscribed: true` after an
     * external teardown (a signaling reconnect disposes every receiver peer
     * while the members stay registered) and never re-subscribe — a black tile
     * until the next visibility change. When the peer of a held subscription
     * stays reported dead for peerLostGraceMs, the subscription is demoted to
     * unsubscribed WITHOUT emitting an unsubscribe (the peer is already gone),
     * which lets the normal reconcile re-subscribe a still-visible member.
     * The grace window keeps a legitimate transient close — a peer rebuild's
     * close→create gap — from reading as a loss. Reports for unknown or
     * unsubscribed members are ignored; call it on a timer alongside tick.
     */
    reportPeerLiveness(
        memberName: string,
        hasLivePeer: boolean,
        nowMs: number,
    ): void;
    /**
     * Drop all bookkeeping for a member (e.g. they left the meeting) *without*
     * emitting an unsubscribe — the leave path tears the peer down itself, so
     * emitting here would be a redundant close on an already-gone peer.
     */
    forgetMember(memberName: string): void;
    /** Members we currently hold a subscription for. */
    getSubscribedMembers(): string[];
    /** A snapshot of a member's state (incl. last action + when), or undefined. */
    getMemberState(memberName: string): MemberSubscriptionState | undefined;
    /** Forget everything (no callbacks fired). For meeting teardown. */
    reset(): void;
}

/**
 * React-free policy engine deciding *which* remote members we subscribe to
 * video from, given the set currently visible on screen. It exists so the app
 * subscribes only to visible tiles rather than to every participant, and so the
 * decision logic — debounce, rate limiting, flap cancellation — is plain,
 * synchronous and unit-testable in isolation from Redux and the DOM.
 *
 * The design mirrors {@link createSpeakingDetector}: no internal timers, no
 * wall-clock reads. Every method takes an explicit `nowMs`, so tests drive it by
 * advancing a number. Debounced actions that need to fire purely because time
 * passed (no new visibility change) are surfaced by calling {@link tick} on a
 * timer — the manager itself never schedules anything.
 *
 * Convergence rules, per member:
 * - Desired = "is in the visible set". When desired already matches the current
 *   subscription, the member is settled and nothing happens.
 * - On a disagreement (visible-but-not-subscribed, or subscribed-but-hidden) a
 *   pending run starts. The opposite action fires only once the disagreement has
 *   held continuously for the relevant delay — subscribeDelayMs going up,
 *   unsubscribeDelayMs coming down. If the member flaps back to agreement before
 *   then, the pending run is cancelled and no action is emitted. This is what
 *   stops sub/unsub spam.
 * - A rate limit (minResubscribeMs) additionally forbids acting on a member
 *   within that window of its last action; the pending run is held (not dropped)
 *   and retried on a later tick, so the action is delayed rather than lost.
 * - Self-healing: the caller reports peer liveness ({@link
 *   VideoSubscriptionManager.reportPeerLiveness}); a held subscription whose
 *   peer stays dead past peerLostGraceMs is demoted (no callback — the peer is
 *   already gone) so the rules above re-subscribe it if still visible. This is
 *   what recovers video after a signaling reconnect tears every receiver peer
 *   down while membership — and thus the visible set — never changes.
 */
export const createVideoSubscriptionManager = ({
    onSubscribe,
    onUnsubscribe,
    subscribeDelayMs,
    unsubscribeDelayMs,
    minResubscribeMs,
    peerLostGraceMs,
    log,
}: VideoSubscriptionManagerOptions): VideoSubscriptionManager => {
    const emit = (message: string): void => {
        if (log) {
            log(message);
        }
    };

    // Per-member bookkeeping. A member absent from the map is treated as
    // unsubscribed with no history — records are only created once a member has
    // been visible or acted upon, so the map stays bounded by meeting membership.
    const members = new Map<string, MemberSubscriptionState>();
    // The desired set: members that should currently be subscribed.
    let visible = new Set<string>();

    const ensureMember = (name: string): MemberSubscriptionState => {
        let state = members.get(name);
        if (!state) {
            state = {
                subscribed: false,
                pendingSince: null,
                lastAction: null,
                lastActionAt: null,
                peerSeenLive: false,
                peerLostSince: null,
            };
            members.set(name, state);
        }
        return state;
    };

    // Reconcile a single member toward its desired state, emitting an action only
    // when both the debounce gate and the rate-limit gate allow it.
    const reconcileMember = (
        name: string,
        state: MemberSubscriptionState,
        nowMs: number,
    ): void => {
        const desired = visible.has(name);
        // Settled — clear any leftover pending run and stop.
        if (desired === state.subscribed) {
            state.pendingSince = null;
            return;
        }
        // A disagreeing member must have a start time; setVisibleMembers sets it,
        // but seed it here too so a bare tick still behaves correctly.
        if (state.pendingSince === null) {
            state.pendingSince = nowMs;
        }
        const debounceMs = desired ? subscribeDelayMs : unsubscribeDelayMs;
        if (nowMs - state.pendingSince < debounceMs) {
            return; // still within the debounce window
        }
        if (
            state.lastActionAt !== null &&
            nowMs - state.lastActionAt < minResubscribeMs
        ) {
            // Rate-limited: keep the pending run alive so a later tick retries
            // once the window has elapsed — the action is deferred, not dropped.
            return;
        }
        const heldMs = nowMs - state.pendingSince;
        state.subscribed = desired;
        state.pendingSince = null;
        state.lastAction = desired ? 'subscribe' : 'unsubscribe';
        state.lastActionAt = nowMs;
        // Loss-detection re-arms per action: the peer this action creates (or
        // destroys) hasn't been observed yet, so stale liveness from the
        // previous peer must not count for the new one.
        state.peerSeenLive = false;
        state.peerLostSince = null;
        if (desired) {
            emit(`subscribe "${name}" (visible ${heldMs}ms before firing)`);
            onSubscribe(name);
        } else {
            emit(`unsubscribe "${name}" (hidden ${heldMs}ms before firing)`);
            onUnsubscribe(name);
        }
    };

    const flush = (nowMs: number): void => {
        members.forEach((state, name) => {
            if (state.pendingSince !== null) {
                reconcileMember(name, state, nowMs);
            }
        });
    };

    return {
        setVisibleMembers(memberNames, nowMs) {
            visible = new Set(memberNames);
            // Track any newly-visible member so the reconcile loop considers it.
            visible.forEach((name) => ensureMember(name));
            // Start a pending run for every member whose desired state disagrees
            // with its subscription, and cancel it the instant they agree again
            // (the flap cancellation that prevents churn).
            members.forEach((state, name) => {
                const desired = visible.has(name);
                if (desired === state.subscribed) {
                    if (state.pendingSince !== null) {
                        // The member reverted to agreement before its debounce
                        // fired — the flap cancellation that prevents sub/unsub
                        // spam. Log it so that non-churn is observable.
                        emit(
                            `cancelled pending ${
                                state.subscribed ? 'unsubscribe' : 'subscribe'
                            } for "${name}" (reverted before the debounce elapsed)`,
                        );
                    }
                    state.pendingSince = null;
                } else if (state.pendingSince === null) {
                    state.pendingSince = nowMs;
                }
            });
            flush(nowMs);
        },
        tick(nowMs) {
            flush(nowMs);
        },
        reportPeerLiveness(name, hasLivePeer, nowMs) {
            const state = members.get(name);
            // Liveness only means something for a subscription we believe we
            // hold — an unsubscribed member's peer is legitimately dead.
            if (!state?.subscribed) {
                return;
            }
            if (hasLivePeer) {
                state.peerSeenLive = true;
                state.peerLostSince = null;
                return;
            }
            // Dead but never seen live: our subscribe's peer setup is still in
            // flight — that's the setup path's business, not a loss.
            if (!state.peerSeenLive) {
                return;
            }
            if (state.peerLostSince === null) {
                state.peerLostSince = nowMs;
                return;
            }
            if (nowMs - state.peerLostSince < peerLostGraceMs) {
                return;
            }
            // The peer we held was torn down behind our back (e.g. a signaling
            // reconnect's leaveVideoRoom) and stayed dead past the grace. Demote
            // to unsubscribed WITHOUT a callback — there is nothing left to
            // close — and, if the member is still visible, start the pending run
            // here so the next flush re-subscribes even though no visibility
            // change will arrive to start it.
            const lostForMs = nowMs - state.peerLostSince;
            state.subscribed = false;
            state.peerSeenLive = false;
            state.peerLostSince = null;
            state.pendingSince = visible.has(name) ? nowMs : null;
            emit(
                `peer for subscribed "${name}" gone for ${lostForMs}ms (external teardown, e.g. reconnect) — dropped the held subscription${
                    state.pendingSince !== null ? '; re-subscribing' : ''
                }`,
            );
        },
        forgetMember(name) {
            members.delete(name);
            visible.delete(name);
        },
        getSubscribedMembers() {
            const result: string[] = [];
            members.forEach((state, name) => {
                if (state.subscribed) {
                    result.push(name);
                }
            });
            return result;
        },
        getMemberState(name) {
            const state = members.get(name);
            return state ? { ...state } : undefined;
        },
        reset() {
            members.clear();
            visible = new Set();
        },
    };
};
